import React from 'react';

export interface DiffEntry {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  changes: Record<string, { from: unknown; to: unknown }>;
}

export interface DiffSide {
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
  changed: DiffEntry[];
}

export interface ComparisonResult {
  questions: DiffSide;
  groups: DiffSide;
}

export const diffTotal = (comparison: ComparisonResult): number =>
  [comparison.questions, comparison.groups].reduce(
    (sum, side) => sum + side.added.length + side.removed.length + side.changed.length,
    0
  );

export const DiffSection: React.FC<{
  title: string;
  side: DiffSide;
  labelKey: string;
}> = ({ title, side, labelKey }) => (
  <div>
    <div className="text-xs font-semibold text-gray-700 mb-1">{title}</div>
    <div className="grid grid-cols-3 gap-3 text-xs">
      <div>
        <div className="text-green-700 font-medium mb-1">
          Added ({side.added.length})
        </div>
        {side.added.map((item, i) => (
          <div key={i} className="bg-green-50 rounded px-2 py-1 mb-1">
            {String(item[labelKey] ?? item.slug ?? '')}
          </div>
        ))}
      </div>
      <div>
        <div className="text-red-700 font-medium mb-1">
          Removed ({side.removed.length})
        </div>
        {side.removed.map((item, i) => (
          <div key={i} className="bg-red-50 rounded px-2 py-1 mb-1">
            {String(item[labelKey] ?? item.slug ?? '')}
          </div>
        ))}
      </div>
      <div>
        <div className="text-yellow-700 font-medium mb-1">
          Changed ({side.changed.length})
        </div>
        {side.changed.map((entry, i) => {
          const label = String(
            entry.after[labelKey] ?? entry.before[labelKey] ?? entry.after.slug ?? entry.before.slug ?? ''
          );
          return (
            <div key={i} className="bg-yellow-50 rounded px-2 py-1 mb-1">
              <div className="font-medium text-gray-800">{label}</div>
              {Object.entries(entry.changes).map(([field, diff]) => (
                <div key={field}>
                  {field}: {JSON.stringify(diff.from)} → {JSON.stringify(diff.to)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

export const VersionDiff: React.FC<{ comparison: ComparisonResult }> = ({
  comparison,
}) => (
  <div className="space-y-4">
    <DiffSection title="Questions" side={comparison.questions} labelKey="prompt" />
    <DiffSection title="Groups" side={comparison.groups} labelKey="label" />
  </div>
);

export const NoChangesNotice: React.FC = () => (
  <div className="text-xs text-gray-500 bg-gray-50 border rounded px-3 py-2">
    No differences from the comparison version — questions, groups, and their
    settings are identical.
  </div>
);
